/**
 * DOCUMENTATION:
 * Component: GCPCaseStudyMountkirk
 * Purpose: Architectural analysis of the Mountkirk Games case study for PCA exam.
 * * * Note for Junior Developers:
 * - Mountkirk Games is moving from a legacy "on-prem" style to a "cloud-native" style.
 * - Key Problem: They cannot predict how many players will join. The system MUST scale automatically.
 * - Key Problem: Global players need low latency.
 */

import React from "react";
import { Link } from "react-router-dom";
import {
    Box, Card, Typography, Grid, Avatar, Button, styled, useTheme, useMediaQuery, Stack,
    Alert, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Divider
} from "@mui/material";

/* ✅ Math Rendering */
import 'katex/dist/katex.min.css';
import { BlockMath } from 'react-katex';

/* Icons */
import SportsEsportsIcon from '@mui/icons-material/SportsEsports';
import GroupsIcon from '@mui/icons-material/Groups';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import QueryStatsIcon from '@mui/icons-material/QueryStats';
import PublicIcon from '@mui/icons-material/Public';
import SpeedIcon from '@mui/icons-material/Speed';

const PageWrapper = styled(Box)(({ theme }) => ({
    minHeight: "100vh",
    backgroundColor: "#0a192d",
    paddingBottom: "80px",
    paddingTop: theme.spacing(12),
    color: "white",
}));

const SectionCard = styled(Card)(({ theme }) => ({
    backgroundColor: "rgba(25, 40, 65, 0.85)",
    borderRadius: "14px",
    padding: theme.spacing(4),
    marginBottom: "25px",
    border: "1px solid rgba(66, 133, 244, 0.2)",
    color: "white",
}));

export default function GCPCaseStudyMountkirk() {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

    return (
        <PageWrapper>
            <Box sx={{ textAlign: "center", mb: 6 }}>
                <Typography variant={isMobile ? "h4" : "h2"} fontWeight={900} color="#4285F4">
                    CASE STUDY: MOUNTKIRK GAMES
                </Typography>
                <Typography variant="subtitle1" sx={{ opacity: 0.6 }}>Scaling Global Multiplayer Backends</Typography>
            </Box>

            <Grid container spacing={4} sx={{ px: { xs: 2, md: 8 } }}>

                {/* 1. The Business Challenge */}
                <Grid item xs={12} md={5}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <GroupsIcon sx={{ color: "#EA4335", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Current State</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 2, opacity: 0.9, lineHeight: 1.8 }}>
                            Mountkirk has a hit game. Their current infrastructure (managed by a third party) is
                            **overwhelmed**. They have no visibility into logs and cannot scale to meet demand.
                        </Typography>
                        <Divider sx={{ my: 2, bgcolor: "rgba(255,255,255,0.1)" }} />
                        <Typography variant="subtitle2" color="#4285F4">Primary Objectives:</Typography>
                        <Typography variant="caption" component="div" sx={{ mt: 1 }}>
                            • Scale rapidly and globally.<br/>
                            • Analyze game data in real-time.<br/>
                            • Modernize to a container-based architecture.<br/>
                            • Move away from Managed SQL to a scalable solution.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* 2. The Proposed Solution Table */}
                <Grid item xs={12} md={7}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SportsEsportsIcon sx={{ color: "#34A853", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Architectural Blueprint</Typography>
                        </Stack>

                        <TableContainer component={Paper} sx={{ bgcolor: "rgba(0,0,0,0.2)" }}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>Requirement</TableCell>
                                        <TableCell sx={{ color: "#4285F4", fontWeight: 700 }}>GCP Service</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Compute (Game Servers)</TableCell>
                                        <TableCell sx={{ color: "#34A853" }}>Google Kubernetes Engine (GKE)</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Game State / Profiles</TableCell>
                                        <TableCell sx={{ color: "#34A853" }}>Cloud Spanner (Global Consistency)</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Real-time Telemetry</TableCell>
                                        <TableCell sx={{ color: "#34A853" }}>Pub/Sub & Dataflow</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Historical Analytics</TableCell>
                                        <TableCell sx={{ color: "#34A853" }}>BigQuery</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        <TableCell sx={{ color: "white" }}>Leaderboards (High TPS)</TableCell>
                                        <TableCell sx={{ color: "#34A853" }}>Cloud Bigtable</TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </SectionCard>
                </Grid>

                {/* 3. Deep Dive: Global Traffic */}
                <Grid item xs={12}>
                    <SectionCard>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <PublicIcon sx={{ color: "#4285F4", fontSize: 35 }} />
                            <Typography variant="h5" fontWeight={800}>Designing for the Global Player</Typography>
                        </Stack>
                        <Typography variant="body1" sx={{ mb: 3, opacity: 0.9 }}>
                            Mountkirk needs to serve users in North America, Europe, and Asia. We achieve this
                            by using a **Global Load Balancer** with a single Anycast IP.
                        </Typography>



                        <Grid container spacing={3} sx={{ mt: 2 }}>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                    <Typography variant="h6" color="#4285F4">Multi-Regional GKE</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Deploy clusters in multiple regions. Use <strong>Multi-cluster Ingress</strong> to route
                                        traffic to the nearest healthy cluster based on proximity.
                                    </Typography>
                                </Box>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderRadius: "8px" }}>
                                    <Typography variant="h6" color="#34A853">Cloud Spanner</Typography>
                                    <Typography variant="body2" sx={{ opacity: 0.7 }}>
                                        Relational data across regions. Provides global strong consistency so a player
                                        can buy an item in London and see it immediately in Tokyo.
                                    </Typography>
                                </Box>
                            </Grid>
                        </Grid>
                    </SectionCard>
                </Grid>

                {/* 4. Analytics & SRE */}
                <Grid item xs={12} md={6}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <QueryStatsIcon sx={{ color: "#FBBC04" }} />
                            <Typography variant="h5" fontWeight={800}>The Analytics Loop</Typography>
                        </Stack>
                        <Typography variant="body2" sx={{ mb: 3, opacity: 0.8 }}>
                            Mountkirk must analyze billions of events to balance game difficulty.
                        </Typography>



                        <Box sx={{ p: 2, bgcolor: "rgba(0,0,0,0.3)", borderRadius: "8px", color: "#FBBC04" }}>
                            <BlockMath math="\text{Throughput} > 10^6 \text{ events/sec}" />
                        </Box>
                        <Typography variant="caption" sx={{ display: 'block', mt: 2, opacity: 0.6 }}>
                            We use <strong>Pub/Sub</strong> to ingest these events and <strong>Dataflow</strong> to
                            enrich them before storing them in <strong>BigQuery</strong> for data scientists.
                        </Typography>
                    </SectionCard>
                </Grid>

                {/* 5. Cost Optimization */}
                <Grid item xs={12} md={6}>
                    <SectionCard sx={{ height: '100%' }}>
                        <Stack direction="row" spacing={2} alignItems="center" mb={3}>
                            <SpeedIcon sx={{ color: "#EA4335" }} />
                            <Typography variant="h5" fontWeight={800}>Operational Efficiency</Typography>
                        </Stack>
                        <Stack spacing={2}>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderLeft: "4px solid #EA4335", borderRadius: "4px" }}>
                                <Typography variant="subtitle2">Managed Instance Groups (MIGs)</Typography>
                                <Typography variant="caption">Use for legacy backend services that cannot be containerized yet. Enable Autoscaling based on CPU.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderLeft: "4px solid #FBBC04", borderRadius: "4px" }}>
                                <Typography variant="subtitle2">Spot VMs (Preemptible)</Typography>
                                <Typography variant="caption">Use for batch analytics and non-critical workloads to save up to 90%.</Typography>
                            </Box>
                            <Box sx={{ p: 2, bgcolor: "rgba(255,255,255,0.03)", borderLeft: "4px solid #34A853", borderRadius: "4px" }}>
                                <Typography variant="subtitle2">Google Cloud Storage</Typography>
                                <Typography variant="caption">Store large game assets and downloads. Use <strong>Multi-Regional</strong> class for high availability.</Typography>
                            </Box>
                        </Stack>
                    </SectionCard>
                </Grid>

                {/* Final Architect Summary */}
                <Grid item xs={12}>
                    <Alert severity="success" sx={{ bgcolor: "rgba(52, 168, 83, 0.1)", color: "white", border: "1px solid #34A853" }}>
                        <strong>PCA EXAM TIP:</strong> Whenever Mountkirk mentions "Global Consistency" and "Transactional Data," the answer is almost always <strong>Cloud Spanner</strong>. If they mention "millions of events per second" for leaderboards, the answer is <strong>Cloud Bigtable</strong>.
                    </Alert>
                </Grid>

                <Grid item xs={12} textAlign="center">
                    <Link to="/gcp-dashboard" style={{ textDecoration: "none" }}>
                        <Button variant="contained" sx={{ bgcolor: "#4285F4", color: "white", fontWeight: 800, px: 6, py: 1.5, borderRadius: "999px" }}>
                            BACK TO DASHBOARD
                        </Button>
                    </Link>
                </Grid>
            </Grid>
        </PageWrapper>
    );
}
